
import { Button } from "@/components/ui/button";
import { ChevronRight, Bot, CalendarClock, Sparkles, ShieldCheck, ArrowDown, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { NetworkAnimation } from "./NetworkAnimation";
import { LightRays } from "./LightRays";
import { CountdownTimer } from "./CountdownTimer";
import { RegistrationForm } from "./RegistrationForm";
import { CareerAdvisorDialog } from "./CareerAdvisorDialog";
import { useSettings } from "@/contexts/SettingsContext";
import { formatArmenianDateTime } from "@/utils/dateFormatting";
import { useI18n } from "@/i18n/LanguageContext";
import { Reveal, Float } from "@/components/motion";

const TECHNOLOGIES = [
  "HTML / CSS", "JavaScript", "TypeScript", "React", "Node.js", "Python", "Java", "C#", "C++",
  "SQL", "Linux", "Networking", "Ethical Hacking", "ChatGPT", "Machine Learning", "Git", "QA Automation", "Scratch",
];

const MarqueeTrack = ({ hidden = false }: { hidden?: boolean }) => (
  <ul className="marquee-track" aria-hidden={hidden || undefined}>
    {TECHNOLOGIES.map((tech) => (
      <li
        key={tech}
        className="whitespace-nowrap rounded-md border border-border/60 bg-card/70 px-3 py-1.5 text-xs font-semibold text-muted-foreground sm:text-sm"
      >
        {tech}
      </li>
    ))}
  </ul>
);

export const Hero = () => {
  const { getSetting, loading } = useSettings();
  const { t } = useI18n();

  const admissionDeadline = getSetting('admission_deadline');
  const formattedDeadline = admissionDeadline ? formatArmenianDateTime(admissionDeadline) : '15.07.2025 22:00';

  const deadlineDate = admissionDeadline ? new Date(admissionDeadline) : new Date('2025-07-15T22:00:00');
  const admissionOpen = !Number.isNaN(deadlineDate.getTime()) && deadlineDate.getTime() > Date.now();

  return (
    <section className="relative w-full overflow-hidden bg-background text-foreground">
      {/* Grid pattern */}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.18)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border)/0.18)_1px,transparent_1px)] bg-[size:48px_48px] [mask-image:linear-gradient(to_bottom,black,transparent_90%)]" />

      {/* Ambient layers (fade back on light theme) */}
      <div className="hero-fx pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: 1 }}>
        <NetworkAnimation />
      </div>
      <div className="hero-fx pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: 2 }}>
        <LightRays
          raysOrigin="top-center"
          raysColor="#007558"
          raysSpeed={1}
          lightSpread={0.5}
          rayLength={3}
          pulsating={false}
          fadeDistance={1}
          saturation={1}
          followMouse
          mouseInfluence={0.1}
          noiseAmount={0}
          distortion={0}
        />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-10 pt-10 sm:px-6 sm:pt-14 lg:px-8 lg:pb-14 lg:pt-20">
        <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:gap-12">
          {/* Copy column */}
          <div className="min-w-0 text-left">
            <div className="mb-6 flex justify-start animate-fade-in">
              <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-primary/30 bg-secondary px-4 py-2 text-sm font-semibold text-highlight">
                <span className={`h-2 w-2 shrink-0 rounded-full ${admissionOpen ? 'animate-pulse bg-primary' : 'bg-muted-foreground'}`} />
                <span className="truncate">
                  {admissionOpen
                    ? `${t.hero.badge} · ${t.hero.until} ${formattedDeadline}`
                    : t.hero.closed}
                </span>
              </div>
            </div>

            {/* LCP element: rendered immediately, no JS-driven entrance */}
            <h1 className="lcp-optimized font-display text-[2.15rem] font-extrabold leading-[1.08] text-foreground [hyphens:manual] xs:text-[2.6rem] sm:text-5xl md:text-[3.4rem] lg:text-5xl xl:text-[3.5rem] 2xl:text-6xl">
              {t.hero.titleLead}{" "}
              <span className="bg-gradient-to-r from-primary to-highlight bg-clip-text text-transparent">{t.hero.titleAccent}</span>
            </h1>

            <p className="mt-5 flex items-center justify-start gap-2 text-base font-semibold text-highlight sm:text-lg">
              <Sparkles className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <span>{t.hero.subtitle}</span>
            </p>

            <p className="lcp-optimized mt-4 max-w-2xl text-left text-base leading-7 text-muted-foreground sm:text-lg">
              {t.hero.description}
            </p>

            <div className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-start">
              <RegistrationForm>
                <Button size="lg" className="h-12 px-7 text-base">
                  {t.hero.ctaRegister}
                  <ChevronRight className="ml-1 h-5 w-5" />
                </Button>
              </RegistrationForm>

              <CareerAdvisorDialog>
                <Button variant="outline" size="lg" className="h-12 px-7 text-base">
                  <Bot className="mr-2 h-5 w-5" />
                  {t.hero.ctaAdvisor}
                </Button>
              </CareerAdvisorDialog>
            </div>

            <ul className="mt-7 grid grid-cols-1 gap-x-6 gap-y-2 text-sm text-muted-foreground xs:grid-cols-2 lg:max-w-xl">
              {t.hero.chips.map((chip) => (
                <li key={chip} className="flex items-center justify-start gap-2">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                    <Check className="h-3 w-3" aria-hidden="true" />
                  </span>
                  <span className="text-left">{chip}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* App-card column */}
          <div className="relative mx-auto w-full max-w-md min-w-0 lg:max-w-none">
            <div className="glow-blob pointer-events-none absolute -inset-8 -z-10 opacity-70" aria-hidden="true" />

            <Reveal delay={0.05} className="app-card relative rounded-xl border-primary/40 bg-card/90 p-5 backdrop-blur-md sm:p-6">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-primary">
                    <CalendarClock className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {t.hero.admission}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">{t.hero.admissionHint}</p>
                </div>
                <span className="shrink-0 rounded-md bg-secondary px-2.5 py-1 text-xs font-bold text-highlight">{formattedDeadline}</span>
              </div>
              {!loading && <CountdownTimer deadline={admissionDeadline} size="compact" />}
              <RegistrationForm>
                <Button className="mt-4 h-11 w-full">
                  {t.common.registerNow}
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </RegistrationForm>
            </Reveal>

            <div className="mt-4 grid grid-cols-1 gap-4 xs:grid-cols-[1.4fr_1fr]">
              <Reveal delay={0.15}>
                <Float amplitude={4} duration={7}>
                  <Link
                    to="/ai-teacher-training"
                    className="app-card block h-full rounded-xl p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-5"
                  >
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2 py-0.5 text-[11px] font-extrabold uppercase tracking-wide text-primary-foreground">
                      <Sparkles className="h-3 w-3" aria-hidden="true" />
                      {t.hero.featuredLabel}
                    </span>
                    <p className="mt-3 font-display text-lg font-bold leading-snug text-foreground">{t.hero.featuredTitle}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{t.hero.featuredMeta}</p>
                    <p className="mt-3 text-base font-bold text-highlight">{t.hero.featuredPrice}</p>
                  </Link>
                </Float>
              </Reveal>

              <Reveal delay={0.25}>
                <Float amplitude={5} duration={8} delay={0.6}>
                  <a
                    href="#courses"
                    className="app-card flex h-full flex-col justify-between rounded-xl p-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-5"
                  >
                    <ShieldCheck className="h-6 w-6 text-primary" aria-hidden="true" />
                    <div className="mt-4">
                      <p className="font-display text-3xl font-extrabold text-foreground">6</p>
                      <p className="text-sm font-semibold text-foreground">{t.hero.directions}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">{t.hero.directionsHint}</p>
                    </div>
                  </a>
                </Float>
              </Reveal>
            </div>
          </div>
        </div>

        {/* Technology marquee */}
        <div className="mt-12 lg:mt-16">
          <p className="mb-3 text-center text-xs font-bold uppercase tracking-wide text-muted-foreground">{t.hero.marqueeLabel}</p>
          <div className="marquee">
            <MarqueeTrack />
            <MarqueeTrack hidden />
          </div>
        </div>

        <div className="mt-8 flex justify-center">
          <a
            href="#courses"
            className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold text-muted-foreground transition-colors hover:text-highlight"
          >
            {t.hero.scrollHint}
            <ArrowDown className="h-4 w-4 animate-bounce" aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
};
